import Header from "@/components/layouts/Header";
import ReportHeader from "@/components/ReportHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import DateField from "@/components/ui/date-field";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ReportButton } from "@/components/ui/report-button";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import VehicleSelect from "@/components/ui/vehicle-select";
import { MapStateProvider } from "@/contexts/MapStateContext";
import { errorHandler, formatToBrl, toggleVisibility } from "@/lib/utils";
import {
  trackReportsService,
  type DayTotals,
  type Travel,
} from "@/services/api/trackReports";
import { zodResolver } from "@hookform/resolvers/zod";
import { differenceInDays, format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Hourglass,
  Map,
  Route,
} from "lucide-react";
import { Fragment, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import TravelPositionsDialog from "./TravelPositionsDialog";

const MAX_PERIOD_DAYS = 3;

const formSchema = z
  .object({
    vehicle_id: z
      .number({
        message: "Veículo é obrigatório",
      })
      .min(0, "Veículo é obrigatório"),
    start_hour: z.string(),
    end_hour: z.string(),
    start_date: z.string().min(1, "Data início é obrigatória"),
    end_date: z.string().min(1, "Data fim é obrigatória"),
  })
  .refine(
    (data) => {
      if (!data.start_date || !data.end_date) return true;
      return new Date(data.end_date) >= new Date(data.start_date);
    },
    {
      message: "Data fim deve ser igual ou posterior à data início.",
      path: ["end_date"],
    },
  )
  .refine(
    (data) => {
      if (!data.start_date || !data.end_date) return true;
      const diff = differenceInDays(
        new Date(data.end_date),
        new Date(data.start_date),
      );
      return diff < MAX_PERIOD_DAYS;
    },
    {
      message: `O período não pode ser maior que ${MAX_PERIOD_DAYS} dias.`,
      path: ["end_date"],
    },
  );

type FormValues = z.infer<typeof formSchema>;

const TravelReport = () => {
  const [selectedTravelData, setSelectedTravelData] = useState<Travel>();
  const [isFormVisible, setIsFormVisible] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startHour, setStartHour] = useState("");
  const [endHour, setEndHour] = useState("");
  const [collapsedDays, setCollapsedDays] = useState<Record<string, boolean>>(
    {},
  );
  const [reportData, setReportData] = useState({
    travels: [] as Travel[],
    totals: {
      average_distance_between_travels: 0,
      average_time_between_travels: "",
      average_time_to_travel_one_km: "",
      average_speed: 0,
      distance: 0,
      max_speed: 0,
      over_speed_count: 0,
      over_speed_time: "",
      speed_average: 0,
      time: "",
    },
    day_totals: {} as Record<string, DayTotals>,
    vehicle_title: "",
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      vehicle_id: undefined,
      start_hour: "00:00",
      end_hour: "23:59",
      start_date: format(new Date(), "yyyy-MM-dd"),
      end_date: format(new Date(), "yyyy-MM-dd"),
    },
  });

  const onSubmit = async (data: FormValues) => {
    setStartHour(data.start_hour);
    setEndHour(data.end_hour);
    setStartDate(data.start_date);
    setEndDate(data.end_date);
    try {
      const params = {
        vehicle_id: data.vehicle_id,
        start_hour: data.start_hour,
        end_hour: data.end_hour,
        start_date: data.start_date,
        end_date: data.end_date,
      };
      const response = await trackReportsService.getTravelReport(params);
      setReportData({
        totals: {
          ...response.data.totals,
          average_distance_between_travels:
            response.data.totals.avarage_distance_between_travels,
          average_time_between_travels:
            response.data.totals.avarage_time_between_travels,
          average_time_to_travel_one_km:
            response.data.totals.avarage_time_to_travel_one_km,
        },
        day_totals: response.data.day_totals,
        travels: response.data.travels,
        vehicle_title: response.data.vehicle_title,
      });
      setCollapsedDays({});
      setIsFormVisible(false);
    } catch (error) {
      errorHandler(error, "Erro ao buscar relatório.");
    }
  };

  const handleToggleFormVisibility = () => {
    toggleVisibility(isFormVisible, setIsFormVisible);
  };

  const formatTimeInput = (
    value: string,
    onChange: (value: string) => void,
  ) => {
    let cleanValue = value.replace(/\D/g, "");

    if (cleanValue.length > 4) {
      cleanValue = cleanValue.substring(0, 4);
    }

    if (cleanValue.length >= 3) {
      const hours = parseInt(cleanValue.substring(0, 2), 10);
      if (hours > 23) {
        cleanValue = "23" + cleanValue.substring(2);
      }

      if (cleanValue.length >= 4) {
        const minutes = parseInt(cleanValue.substring(2, 4), 10);
        if (minutes > 59) {
          cleanValue = cleanValue.substring(0, 2) + "59";
        }
      }

      cleanValue = cleanValue.substring(0, 2) + ":" + cleanValue.substring(2);
    }

    onChange(cleanValue);
  };

  const formatTime = (time: string) => {
    const hourMinute = time.split(":").slice(0, 2);
    return `${hourMinute[0]}h ${hourMinute[1]}m`;
  };

  const formatDriver = (
    driversWithColors?: Array<{ name: string; color: string }>,
  ) => {
    if (!driversWithColors || driversWithColors.length === 0) {
      return "";
    }

    return <span>{driversWithColors[0].name}</span>;
  };

  const groupTravelsByDate = (travels: Travel[]) => {
    const groups: Record<string, Travel[]> = {};
    travels.forEach((t) => {
      const d = t.date;
      if (!groups[d]) groups[d] = [];
      groups[d].push(t);
    });
    return Object.keys(groups)
      .sort()
      .map((date) => ({ date, travels: groups[date] }));
  };

  const travelRowKey = (travel: Travel, index: number) =>
    travel.date
      ? `${travel.date}-${travel.start}-${index}`
      : `${travel.start}-${index}`;

  const handleMapButtonClick = (travel: Travel) => {
    setSelectedTravelData(travel);
    setDialogOpen(true);
  };

  const toggleDayCollapsed = (date: string) => {
    setCollapsedDays((prev) => ({ ...prev, [date]: !prev[date] }));
  };

  const handleExportPdf = async () => {
    setPdfLoading(true);
    try {
      const params = {
        vehicle_id: form.getValues("vehicle_id"),
        start_date: startDate,
        end_date: endDate,
        start_hour: startHour,
        end_hour: endHour,
      };

      const blob = await trackReportsService.getTravelReportPdf(params);
      const pdfBlob = new Blob([blob], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `relatorio-trajetos-${params.vehicle_id}-${startDate}-${endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(pdfUrl);
    } catch (error) {
      errorHandler(error, "Erro ao gerar PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="h-full">
      <Header
        title="Trajetos"
        showToggleButton
        isFormVisible={isFormVisible}
        onToggleFormVisibility={handleToggleFormVisibility}
      />

      <div
        className={`max-w-md transition-all duration-300 ease-in-out ${
          isFormVisible ? "opacity-100" : "opacity-0 max-h-0 overflow-hidden"
        }`}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="vehicle_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Veículo</FormLabel>
                  <VehicleSelect
                    value={field.value}
                    onChange={field.onChange}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <DateField form={form} name="start_date" label="Data início" />
            <DateField form={form} name="end_date" label="Data fim" />

            <FormField
              control={form.control}
              name="start_hour"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Horário inicial</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      className="w-full"
                      placeholder="__:__"
                      maxLength={5}
                      value={field.value}
                      onChange={(e) =>
                        formatTimeInput(e.target.value, field.onChange)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="end_hour"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Horário final</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      className="w-full"
                      placeholder="__:__"
                      maxLength={5}
                      value={field.value}
                      onChange={(e) =>
                        formatTimeInput(e.target.value, field.onChange)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <ReportButton loading={form.formState.isSubmitting} />
          </form>
        </Form>
      </div>

      {!isFormVisible && (
        <div
          className={`w-full flex flex-col gap-4 transition-all duration-300 ease-in-out ${
            isFormVisible ? "opacity-0 max-h-0 overflow-hidden" : "opacity-100"
          }`}
        >
          {/* Cabecalho do relatorio */}
          <ReportHeader
            handleExportPdf={handleExportPdf}
            primaryInfo={reportData.vehicle_title}
            secondaryInfo={
              startDate === endDate
                ? `${startDate?.split("-").reverse().join("/")} • ${startHour} até ${endHour}`
                : `${startDate?.split("-").reverse().join("/")} a ${endDate?.split("-").reverse().join("/")} • ${startHour} até ${endHour}`
            }
            loading={pdfLoading}
          />

          {/* Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 min-h-[140px]">
            <Card className="group relative cursor-pointer transition-colors duration-300 hover:bg-blue-400">
              <CardContent className="h-full flex flex-col items-center justify-center py-6">
                <div className="flex flex-col items-center gap-2 group-hover:opacity-0 transition-opacity duration-300">
                  <Hourglass className="w-6 h-6" />
                  <div className="mt-2">
                    {reportData.travels.length
                      ? formatTime(
                          reportData.totals.average_time_between_travels,
                        )
                      : "00:00"}
                  </div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white">
                  <span className="text-lg font-medium">
                    Tempo médio/trajetos
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="group relative cursor-pointer transition-colors duration-300 hover:bg-blue-400">
              <CardContent className="h-full flex flex-col items-center justify-center py-6">
                <div className="flex flex-col items-center gap-2 group-hover:opacity-0 transition-opacity duration-300">
                  <Route className="w-6 h-6" />
                  <div className="mt-2">
                    {reportData.totals.average_distance_between_travels} km
                  </div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white">
                  <span className="text-lg font-medium">
                    Distância média/trajetos
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="group relative cursor-pointer transition-colors duration-300 hover:bg-blue-400">
              <CardContent className="h-full flex flex-col items-center justify-center py-6">
                <div className="flex flex-col items-center gap-2 group-hover:opacity-0 transition-opacity duration-300">
                  <Clock className="w-6 h-6" />
                  <div className="mt-2">
                    {reportData.travels.length
                      ? formatTime(
                          reportData.totals.average_time_to_travel_one_km,
                        )
                      : "00:00"}
                  </div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white">
                  <span className="text-lg font-medium">Tempo médio/km</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabela com dados do relatorio */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-center">Mapa</TableHead>
                  <TableHead className="text-center">Início</TableHead>
                  <TableHead className="text-center">Fim</TableHead>
                  <TableHead className="text-center">Duração</TableHead>
                  <TableHead className="text-center">Origem</TableHead>
                  <TableHead className="text-center">Destino</TableHead>
                  <TableHead className="text-center">Motorista</TableHead>
                  <TableHead className="text-center">Vel. média</TableHead>
                  <TableHead className="text-center">Km</TableHead>
                  <TableHead className="text-center">
                    Excesso velocidade
                  </TableHead>
                  <TableHead className="text-center">
                    Tempo excesso velocidade
                  </TableHead>
                  <TableHead className="text-center">Vel. máxima</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupTravelsByDate(reportData.travels).map(
                  ({ date, travels: dayTravels }) => {
                    const dayTotals = reportData.day_totals[date];
                    const isSingleDayReport = startDate === endDate;
                    const isExpanded = !collapsedDays[date];
                    const parsedDate = parseISO(date);

                    return (
                      <Fragment key={date}>
                        <TableRow
                          className="bg-muted font-semibold text-foreground border-t-2 border-border cursor-pointer"
                          role="button"
                          tabIndex={0}
                          aria-expanded={isExpanded}
                          onClick={() => toggleDayCollapsed(date)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggleDayCollapsed(date);
                            }
                          }}
                        >
                          <TableCell colSpan={12}>
                            <div className="flex items-center gap-2">
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 shrink-0" />
                              ) : (
                                <ChevronRight className="w-4 h-4 shrink-0" />
                              )}
                              <span>
                                {format(parsedDate, "EEE, dd/MM/yyyy", {
                                  locale: ptBR,
                                })}
                              </span>
                              {dayTotals && (
                                <span className="font-normal text-muted-foreground text-xs">
                                  · {dayTravels.length} trajetos ·{" "}
                                  {formatTime(dayTotals.time)} ·{" "}
                                  {formatToBrl(dayTotals.distance, 2)} km ·{" "}
                                  {dayTotals.speed_average} km/h
                                </span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        {isExpanded &&
                          dayTravels.map((travel, idx) => (
                            <TableRow
                              key={travelRowKey(travel, idx)}
                              className="dark:text-white"
                            >
                              <TableCell className="text-center border-l-4 border-l-primary/40">
                                <Button
                                  className="dark:text-white dark:bg-muted"
                                  onClick={() => handleMapButtonClick(travel)}
                                >
                                  <MapStateProvider>
                                    <Map className="w-6 h-6" />
                                  </MapStateProvider>
                                </Button>
                              </TableCell>
                              <TableCell className="text-center">
                                {travel.start}
                              </TableCell>
                              <TableCell className="text-center">
                                {travel.end}
                              </TableCell>
                              <TableCell className="text-center">
                                {travel.time}
                              </TableCell>
                              <TableCell className="text-center min-w-[200px]">
                                {travel.start_address?.display_name ? (
                                  <span
                                    className="text-sm"
                                    title={travel.start_address.display_name}
                                  >
                                    {travel.start_address.display_name}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">
                                    -
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-center min-w-[200px]">
                                {travel.end_address?.display_name ? (
                                  <span
                                    className="text-sm"
                                    title={travel.end_address.display_name}
                                  >
                                    {travel.end_address.display_name}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">
                                    -
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-center min-w-[150px]">
                                {formatDriver(travel.drivers_with_colors)}
                              </TableCell>
                              <TableCell className="text-center whitespace-nowrap">
                                {travel.speed_average} km/h
                              </TableCell>
                              <TableCell className="text-center whitespace-nowrap">
                                {formatToBrl(travel.distance, 2)} km
                              </TableCell>
                              <TableCell className="text-center">
                                {travel.over_speed_count}
                              </TableCell>
                              <TableCell className="text-center">
                                {travel.over_speed_time}
                              </TableCell>
                              <TableCell className="text-center whitespace-nowrap">
                                {travel.max_speed.value} km/h
                              </TableCell>
                            </TableRow>
                          ))}
                        {isExpanded && dayTotals && !isSingleDayReport && (
                          <TableRow
                            key={`total-${date}`}
                            className="bg-muted/60 font-medium text-foreground"
                          >
                            <TableCell
                              colSpan={3}
                              className="text-left border-l-4 border-l-primary/40"
                            >
                              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                Total do dia
                              </span>
                            </TableCell>
                            <TableCell className="text-center whitespace-nowrap">
                              {formatTime(dayTotals.time)}
                            </TableCell>
                            <TableCell colSpan={3}></TableCell>
                            <TableCell className="text-center whitespace-nowrap">
                              {dayTotals.speed_average} km/h
                            </TableCell>
                            <TableCell className="text-center whitespace-nowrap">
                              {formatToBrl(dayTotals.distance, 2)} km
                            </TableCell>
                            <TableCell className="text-center">
                              {dayTotals.over_speed_count}
                            </TableCell>
                            <TableCell className="text-center">
                              {dayTotals.over_speed_time
                                .split(":")
                                .slice(0, 2)
                                .join(":")}
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  },
                )}
              </TableBody>
              <TableFooter>
                <TableRow className="font-bold text-primary dark:text-primary text-foreground">
                  <TableCell colSpan={3} className="text-left">
                    <span className="text-xs font-bold uppercase tracking-wide">
                      Total período
                    </span>
                  </TableCell>
                  <TableCell className="text-center whitespace-nowrap">
                    {reportData.travels.length
                      ? formatTime(reportData.totals.time)
                      : ""}
                  </TableCell>
                  <TableCell colSpan={3}></TableCell>
                  <TableCell className="text-center whitespace-nowrap">
                    {reportData.totals.speed_average} km/h
                  </TableCell>
                  <TableCell className="text-center whitespace-nowrap">
                    {formatToBrl(reportData.totals.distance, 2)} km
                  </TableCell>
                  <TableCell className="text-center">
                    {reportData.totals.over_speed_count}
                  </TableCell>
                  <TableCell className="text-center">
                    {reportData.totals.over_speed_time
                      .split(":")
                      .slice(0, 2)
                      .join(":")}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        </div>
      )}

      <TravelPositionsDialog
        period={`${selectedTravelData?.start} até ${selectedTravelData?.end}`}
        vehicleTitle={reportData.vehicle_title}
        positions={selectedTravelData?.positions ?? []}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        driversWithColors={selectedTravelData?.drivers_with_colors}
      />
    </div>
  );
};

export default TravelReport;
